import api from './api';

/**
 * 車隊相關API服務
 */
export const fleetService = {
    /**
     * 獲取車隊列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.limit - 每頁記錄數
     * @param {string} params.status - 狀態過濾
     * @param {string} params.search - 搜尋關鍵字
     * @returns {Promise<{data: Array, meta: Object}>} 車隊列表和元數據
     */
    getFleets: async (params = {}) => {
        const response = await api.get('/fleets', { params });
        return response.data;
    },

    /**
     * 根據ID獲取車隊詳情
     * @param {number|string} fleetId - 車隊ID
     * @returns {Promise<Object>} 車隊詳情
     */
    getFleetById: async (fleetId) => {
        const response = await api.get(`/fleets/${fleetId}`);
        return response.data;
    },

    /**
     * 創建新車隊
     * @param {Object} fleetData - 車隊數據
     * @returns {Promise<Object>} 創建的車隊
     */
    createFleet: async (fleetData) => {
        const response = await api.post('/fleets', fleetData);
        return response.data;
    },

    /**
     * 更新車隊信息
     * @param {number|string} fleetId - 車隊ID
     * @param {Object} fleetData - 車隊數據
     * @returns {Promise<Object>} 更新後的車隊
     */
    updateFleet: async (fleetId, fleetData) => {
        const response = await api.put(`/fleets/${fleetId}`, fleetData);
        return response.data;
    },

    /**
     * 刪除車隊
     * @param {number|string} fleetId - 車隊ID
     * @returns {Promise<void>}
     */
    deleteFleet: async (fleetId) => {
        await api.delete(`/fleets/${fleetId}`);
    },

    /**
     * 獲取車隊統計數據
     * @param {number|string} fleetId - 車隊ID
     * @returns {Promise<Object>} 車隊統計數據
     */
    getFleetStats: async (fleetId) => {
        const response = await api.get(`/fleets/${fleetId}/stats`);
        return response.data;
    },

    /**
     * 獲取車隊司機列表
     * @param {number|string} fleetId - 車隊ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<{data: Array, meta: Object}>} 司機列表和元數據
     */
    getFleetDrivers: async (fleetId, params = {}) => {
        const response = await api.get(`/fleets/${fleetId}/drivers`, { params });
        return response.data;
    },

    /**
     * 獲取車隊訂單列表
     * @param {number|string} fleetId - 車隊ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<{data: Array, meta: Object}>} 訂單列表和元數據
     */
    getFleetOrders: async (fleetId, params = {}) => {
        const response = await api.get(`/fleets/${fleetId}/orders`, { params });
        return response.data;
    },

    /**
     * 更新車隊服務區域
     * @param {number|string} fleetId - 車隊ID
     * @param {Object} serviceArea - 服務區域數據
     * @returns {Promise<Object>} 更新後的車隊
     */
    updateServiceArea: async (fleetId, serviceArea) => {
        const response = await api.put(`/fleets/${fleetId}/service-area`, serviceArea);
        return response.data;
    },

    /**
     * 設置車隊狀態
     * @param {number|string} fleetId - 車隊ID
     * @param {string} status - 狀態
     * @returns {Promise<Object>} 更新後的車隊
     */
    setStatus: async (fleetId, status) => {
        const response = await api.patch(`/fleets/${fleetId}/status`, { status });
        return response.data;
    },

    /**
     * 添加司機到車隊
     * @param {number|string} fleetId - 車隊ID
     * @param {number|string} driverId - 司機ID
     * @returns {Promise<Object>} 操作結果
     */
    addDriver: async (fleetId, driverId) => {
        const response = await api.post(`/fleets/${fleetId}/drivers`, { driver_id: driverId });
        return response.data;
    },

    /**
     * 從車隊中移除司機
     * @param {number|string} fleetId - 車隊ID
     * @param {number|string} driverId - 司機ID
     * @returns {Promise<void>}
     */
    removeDriver: async (fleetId, driverId) => {
        await api.delete(`/fleets/${fleetId}/drivers/${driverId}`);
    }
};

export default fleetService;